<template>
  <v-card outlined class="productSummaryPane">
    <v-card-text v-if="!productId" class="text-center grey--text">Click a product in the list to see all of its data here.</v-card-text>
    <v-progress-linear v-else-if="loading" indeterminate/>
    <v-card-text v-else-if="error" class="error--text">{{error}}</v-card-text>
    <div v-else-if="p">
      <v-card-title class="d-flex flex-row align-start flex-wrap" style="gap: 8px;">
        <div style="flex: 1 1 200px; word-break: break-word;">
          <div class="subtitle-1 font-weight-bold">{{p.name}}</div>
          <div class="caption">ID {{p.id}} <span v-if="p.deleted" class="error--text">(deleted)</span></div>
        </div>
        <div class="d-flex flex-column" style="gap: 6px;">
          <v-btn small color="info" @click="openProductPage"><v-icon small left>mdi-open-in-new</v-icon>Open product page</v-btn>
          <v-btn v-if="canImport" small color="warning" @click="$emit('import-settings', p)"><v-icon small left>mdi-import</v-icon>Copy settings to overwrite</v-btn>
        </div>
      </v-card-title>
      <v-card-text class="pt-0">
        <h4 class="mt-2">General</h4>
        <v-simple-table dense>
          <tbody>
            <tr v-for="row in generalRows" :key="row[0]"><td class="font-weight-medium" style="width: 40%;">{{row[0]}}</td><td>{{row[1]}}</td></tr>
          </tbody>
        </v-simple-table>

        <h4 class="mt-4">Pricing</h4>
        <v-simple-table dense>
          <tbody>
            <tr><td class="font-weight-medium" style="width: 40%;">Regular price</td><td>{{money(p.regularPrice)}}<span v-if="priceUnit"> per {{priceUnit}}</span></td></tr>
            <tr><td class="font-weight-medium">Sale price</td><td>{{money(p.salePrice)}}<span v-if="priceUnit"> per {{priceUnit}}</span></td></tr>
            <tr v-for="b in branchPrices" :key="b.key"><td class="font-weight-medium">{{b.name}}</td><td>{{b.text}}</td></tr>
          </tbody>
        </v-simple-table>

        <h4 class="mt-4">Quantities and rules</h4>
        <v-simple-table dense>
          <tbody>
            <tr v-for="row in ruleRows" :key="row[0]"><td class="font-weight-medium" style="width: 40%;">{{row[0]}}</td><td>{{row[1]}}</td></tr>
          </tbody>
        </v-simple-table>

        <h4 class="mt-4">Used in multiples</h4>
        <v-simple-table dense>
          <tbody>
            <tr v-for="row in multipleRows" :key="row[0]"><td class="font-weight-medium" style="width: 40%;">{{row[0]}}</td><td>{{row[1]}}</td></tr>
          </tbody>
        </v-simple-table>

        <h4 class="mt-4">Categories, tags and suppliers</h4>
        <div class="mb-1"><span class="caption">Categories:</span> <v-chip v-for="c in p.categories" :key="'c'+c" x-small class="mr-1">{{c}}</v-chip><span v-if="!p.categories.length" class="caption grey--text">none</span></div>
        <div class="mb-1"><span class="caption">Tags:</span> <v-chip v-for="t in p.tags" :key="'t'+t" x-small class="mr-1">{{t}}</v-chip><span v-if="!p.tags.length" class="caption grey--text">none</span></div>
        <div class="mb-1"><span class="caption">Suppliers:</span> <v-chip v-for="s in p.suppliers" :key="'s'+s.name" x-small class="mr-1">{{s.name}} ({{money(s.costPrice)}})</v-chip><span v-if="!p.suppliers.length" class="caption grey--text">none</span></div>

        <h4 class="mt-4">Stock by location</h4>
        <v-simple-table dense>
          <thead><tr><th>Location</th><th>Available</th><th>In stock</th><th>Min</th><th>Manage</th></tr></thead>
          <tbody>
            <tr v-for="l in p.locations" :key="l.name">
              <td>{{l.name}}</td><td>{{qty(l.available)}}</td><td>{{qty(l.quantity)}}</td><td>{{qty(l.minQuantity)}}</td><td>{{l.manageStock ? 'Yes' : 'No'}}</td>
            </tr>
            <tr v-if="!p.locations.length"><td colspan="5" class="grey--text">No locations.</td></tr>
          </tbody>
        </v-simple-table>

        <div v-if="p.description" class="mt-4">
          <h4>Description</h4>
          <div class="caption" style="white-space: pre-wrap;">{{p.description}}</div>
        </div>
        <div class="caption mt-4 grey--text">Created {{date(p.createdAt)}} &middot; Updated {{date(p.updatedAt)}}</div>
      </v-card-text>
    </div>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
import axios from "axios";

// Read-only view of one product for the bulk product settings page. It only shows what is stored.
export default {
  name: 'ProductSummaryPane',
  props: {
    productId: {type: Number, default: null},
    // the parent page listens for 'import-settings' and fills its overwrite section from this product
    canImport: {type: Boolean, default: false}
  },
  data(){
    return {
      loading: false,
      error: null,
      p: null
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    meta(){
      return this.p?.metadata || {};
    },
    priceUnit(){
      let c = this.p?.unitConversion;
      return c && c.priceUnit ? c.priceUnit : (c ? c.unit : null);
    },
    generalRows(){
      let p = this.p;
      return [
        ['Type', p.type], ['SKU', p.sku || '-'], ['Barcode', p.barcode || '-'], ['VGC', p.vgc || '-'],
        ['Brand', p.brand || '-'], ['Tax type', p.taxType || '-'],
        ['Visible on ecommerce', this.yn(p.ecomVisible)],
        ['Composite', p.isComposite ? `Yes (${p.compositeProducts} product(s))` : 'No']
      ];
    },
    ruleRows(){
      let m = this.meta;
      return [
        ['Unit type', m.unit || '-'],
        ['Allow fractional', this.yn(m.allowFractional)],
        ['Factor of quantity', m.quantityStepAmount ?? '-'],
        ['Minimum order quantity', m.minSaleQuantity ?? '-'],
        ['Packaging quantity', m.packagingQuantity ?? '-'],
        ['Requires serials', this.yn(m.requireSerials)],
        ['Warranty information', m.warrantyInformation || '-']
      ];
    },
    multipleRows(){
      let m = this.meta;
      if(!m.usedInMultiples) return [['Used in multiples', 'No']];
      let rows = [['Used in multiples', 'Yes']];
      let c = this.p.unitConversion;
      if(c){
        rows.push(['Format', 'Unit conversion']);
        rows.push(['Conversion', c.label]);
        if(c.priceUnit && c.priceUnit !== c.unit){
          rows.push([`Charge by the whole ${c.priceUnit}`, this.yn(m.roundUpConversionQuantity !== false)]);
          rows.push(['Round each unit up first', this.yn(m.roundUpPerUnitConversion === true)]);
        }
      }else{
        rows.push(['Format', 'Custom multiple']);
        rows.push(['Multiple label', m.multipleLabel || '-']);
      }
      rows.push(['Lock value on returns/dispatch', this.yn(m.lockMultipleAmount !== false)]);
      return rows;
    },
    branchPrices(){
      let t = this.p?.priceTable;
      if(!t) return [];
      return Object.keys(t).map(key => {
        let vals = (t[key].values || []).map(v => this.money(v.unitPrice) + (v.condition && v.condition !== 'ANY' ? ` (${v.criterionName || v.condition} ${v.criterionValue ?? ''})` : '')).join(' / ');
        return {key, name: (this.p.priceTableNames && this.p.priceTableNames[key]) || key, text: vals};
      });
    }
  },
  watch: {
    productId: {
      immediate: true,
      handler(){ this.load(); }
    }
  },
  methods: {
    async load(){
      this.p = null;
      this.error = null;
      if(!this.productId) return;
      try{
        this.loading = true;
        let id = this.productId;
        let res = await axios.get(`${this.getEndpoint}/api/inventoryConfigurator/products/${id}/summary`);
        if(id === this.productId) this.p = res.data.data; // ignore a slow answer for a product that is no longer selected
      }catch(e){
        this.error = 'Could not load this product.';
      }finally{
        this.loading = false;
      }
    },
    openProductPage(){
      let href = this.$router.resolve({path: `/products/view/${this.p.id}`}).href;
      window.open(new URL(href, window.location.href).href, '_blank');
    },
    yn(v){ return v ? 'Yes' : 'No'; },
    money(v){ return v === null || v === undefined ? '-' : `$${Number(v).toFixed(2)}`; },
    qty(v){ return v === null || v === undefined ? '-' : parseFloat(Number(v).toFixed(6)); },
    date(d){ return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }
  }
}
</script>
